import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRequestHandler } from "../utils/useRequestHandler";
import Servicios from "../services";
import { formatearFechaDDMMYYHHMM } from "../utils/dates";
import { TransicionEstadoDialog } from "../components/TransicionEstadoDialog";

// Tipos de contenido reportado (Enums.TipoContenidoReportado del backend).
export const TIPO = {
  Mensaje: 1,
  Resenia: 2,
  RespuestaResenia: 3,
  PencaNombre: 4,
  PencaImagen: 5,
  PerfilNombre: 6,
  PerfilFoto: 7
};

// Objetivo de respuesta en temporada (DP-07): 24 h desde el reporte.
export const OBJETIVO_HORAS = 24;

// Revisión G0-E, P6: categoría del reporte y su gravedad (runbook).
export const CATEGORIAS = {
  1: "Amenaza o acoso",
  2: "Sexual o con menores",
  3: "Datos personales",
  4: "Insulto",
  5: "Spam",
  6: "Otro",
  // M1-122 / SV-017: el expulsado reporta el grupo (G3).
  7: "Abuso de administrador"
};

// M1-122 / SV-016: los mensajes anteriores del chat (hasta 5), para ver el
// contexto del mensaje reportado.
export const HiloAnterior = ({ mensajes }) => {
  if (!mensajes || mensajes.length === 0) return null;
  return (
    <details style={{ marginTop: 4 }}>
      <summary>Hilo anterior ({mensajes.length})</summary>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {mensajes.map(m => (
          <li key={m.mensajeId} style={{ opacity: m.activo ? 1 : 0.6 }}>
            <strong>{m.usuarioNombre}:</strong> {m.texto}
          </li>
        ))}
      </ul>
    </details>
  );
};

const TIPOS_IMAGEN = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export const tipoDeImagenPermitido = tipo =>
  TIPOS_IMAGEN.includes((tipo ?? "").toLowerCase()) ? tipo.toLowerCase() : "image/jpeg";

export const etiquetaGravedad = gravedad =>
  gravedad === 1 ? "G1 urgente" : gravedad === 2 ? "G2" : "G3";

/** "hace 5 min", "hace 3 h", "hace 2 d" a partir de la fecha del reporte. */
export const antiguedad = (fecha, ahora = new Date()) => {
  const minutos = Math.max(0, Math.floor((ahora - new Date(fecha)) / 60000));
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return `hace ${Math.floor(horas / 24)} d`;
};

export const horasDesde = (fecha, ahora = new Date()) =>
  (ahora - new Date(fecha)) / 3600000;

/**
 * Qué acciones aplican a una fila de la bandeja. Las del chat van a los
 * endpoints de siempre (Moderacion/Reportes/...); las de contenido a
 * Moderacion/Contenido/{id}/{accion}. Suspender aplica a todo lo que tiene
 * autor identificado.
 */
export const accionesPara = fila => {
  if (fila.origen === "mensaje") {
    return [
      "ignorar",
      "advertir",
      "eliminarMensaje",
      "expulsar",
      "silenciar",
      "suspender"
    ];
  }
  const acciones = ["ignorar"];
  switch (fila.tipoContenido) {
    case TIPO.Resenia:
    case TIPO.RespuestaResenia:
      acciones.push("ocultar", "eliminar");
      break;
    case TIPO.PencaNombre:
    case TIPO.PerfilNombre:
      acciones.push("restablecerNombre");
      break;
    case TIPO.PencaImagen:
    case TIPO.PerfilFoto:
      acciones.push("quitarImagen");
      break;
    default:
      break;
  }
  if (fila.autorUsuarioId) acciones.push("suspender");
  return acciones;
};

const ETIQUETA_ACCION = {
  ignorar: "Ignorar",
  advertir: "Advertir al usuario",
  eliminarMensaje: "Eliminar mensaje",
  expulsar: "Expulsar de la penca",
  silenciar: "Silenciar en el chat de esta penca",
  ocultar: "Ocultar",
  eliminar: "Eliminar",
  restablecerNombre: "Restablecer nombre",
  quitarImagen: "Quitar imagen",
  suspender: "Suspender cuenta del autor"
};

// Acciones de contenido que se resuelven con un PUT simple (ver accionesPara).
const ACCION_CONTENIDO = {
  ignorar: "Ignorar",
  ocultar: "Ocultar",
  eliminar: "Eliminar",
  restablecerNombre: "RestablecerNombre",
  quitarImagen: "QuitarImagen"
};

const DESCRIPCION_CONFIRMACION = {
  ocultar: f =>
    `Se ocultará este contenido de ${f.autorUsuarioNombre} para todos los usuarios de la app. Queda guardado como evidencia.`,
  eliminar: f =>
    `Se eliminará este contenido de ${f.autorUsuarioNombre}. La copia reportada queda guardada como evidencia.`,
  restablecerNombre: f =>
    f.tipoContenido === TIPO.PencaNombre
      ? `La penca pasará a llamarse "Penca <número>" y se borrará su descripción.`
      : `El usuario ${f.autorUsuarioNombre} pasará a llamarse "Usuario <número>".`,
  quitarImagen: () =>
    "La imagen dejará de mostrarse en la app (el archivo queda guardado como evidencia).",
  silenciar: f =>
    `${f.autorUsuarioNombre} ya no podrá escribir en "${f.contexto.replace(/^Chat de /, "")}", pero sigue en la penca y en el ranking.`
};

/**
 * Bandeja de moderación (ADO #65 + M0-12 / BKW-O0-11): reportes pendientes del
 * chat y de reseñas, respuestas, grupos y perfiles, del más viejo al más
 * nuevo, con su antigüedad contra el objetivo de 24 h. Segunda pestaña: la
 * lista de términos que el filtro rechaza en todo texto de usuarios.
 */
export const ModeracionChat = () => {
  const [pestania, setPestania] = useState(0);
  const [reportes, setReportes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [dialogoAccion, setDialogoAccion] = useState(null);
  const [textoAdvertencia, setTextoAdvertencia] = useState("");
  const [diasSuspension, setDiasSuspension] = useState(7);
  const [motivoSuspension, setMotivoSuspension] = useState("");
  const [terminos, setTerminos] = useState([]);
  const [sanciones, setSanciones] = useState({ suspendidos: [], silenciados: [] });
  const [nuevoTermino, setNuevoTermino] = useState("");
  const sendRequest = useRequestHandler();

  useEffect(() => {
    refrescarGrilla();
    refrescarTerminos();
    refrescarSanciones();
  }, []);

  const refrescarSanciones = () => {
    sendRequest(Servicios.ObtenerSancionesActivas, [], data =>
      setSanciones(data?.data ?? { suspendidos: [], silenciados: [] })
    );
  };

  const levantarSuspension = usuario => {
    if (!window.confirm(`¿Levantar la suspensión de ${usuario.nombre}?`)) return;
    sendRequest(Servicios.LevantarSuspension, [usuario.usuarioId], refrescarSanciones);
  };

  const quitarSilencio = s => {
    if (!window.confirm(`¿Dejar que ${s.nombre} vuelva a escribir en ${s.pencaNombre}?`)) return;
    sendRequest(Servicios.QuitarSilencio, [s.pencaId, s.usuarioId], refrescarSanciones);
  };

  // P-01: la evidencia se muestra solo como imagen, en un <img> dentro de la
  // bandeja (nunca se abre como documento), y con un tipo de una allowlist.
  const [evidenciaUrl, setEvidenciaUrl] = useState(null);
  const verEvidencia = fila => {
    sendRequest(Servicios.DescargarEvidencia, [fila.reporteId], respuesta => {
      const tipo = tipoDeImagenPermitido(respuesta.data?.type);
      const blob = new Blob([respuesta.data], { type: tipo });
      setEvidenciaUrl(URL.createObjectURL(blob));
    });
  };
  const cerrarEvidencia = () => {
    if (evidenciaUrl) URL.revokeObjectURL(evidenciaUrl);
    setEvidenciaUrl(null);
  };

  const refrescarGrilla = () => {
    setCargando(true);
    sendRequest(
      Servicios.ObtenerBandejaModeracion,
      [],
      data => {
        setReportes(
          (data?.data ?? []).map(r => ({ ...r, id: `${r.origen}-${r.reporteId}` }))
        );
        setCargando(false);
      },
      () => setCargando(false)
    );
  };

  const refrescarTerminos = () => {
    sendRequest(Servicios.ObtenerTerminosFiltrados, [], data =>
      setTerminos(data?.data ?? [])
    );
  };

  const abrirMenu = (event, fila) => {
    setMenuAnchor(event.currentTarget);
    setSeleccionado(fila);
  };

  const cerrarMenu = () => setMenuAnchor(null);

  const elegirAccion = accion => {
    cerrarMenu();
    if (accion === "ignorar") {
      const promesa =
        seleccionado.origen === "mensaje"
          ? Servicios.IgnorarReporte
          : Servicios.ResolverReporteContenido;
      const params =
        seleccionado.origen === "mensaje"
          ? [seleccionado.reporteId]
          : [seleccionado.reporteId, "Ignorar"];
      sendRequest(promesa, params, refrescarGrilla);
      return;
    }
    setTextoAdvertencia("");
    setDiasSuspension(7);
    setMotivoSuspension("");
    setDialogoAccion(accion);
  };

  const cerrarDialogoAccion = () => {
    setDialogoAccion(null);
    setSeleccionado(null);
  };

  const cerrarConRefresh = () => {
    cerrarDialogoAccion();
    refrescarGrilla();
    refrescarSanciones();
  };

  const agregarTermino = () => {
    const termino = nuevoTermino.trim();
    if (!termino) return;
    sendRequest(Servicios.AgregarTerminoFiltrado, [termino], () => {
      setNuevoTermino("");
      refrescarTerminos();
    });
  };

  const eliminarTermino = termino => {
    if (!window.confirm(`¿Sacar "${termino.termino}" de la lista?`)) return;
    sendRequest(Servicios.EliminarTerminoFiltrado, [termino.id], refrescarTerminos);
  };

  const columns = [
    {
      field: "fechaHoraReporte",
      headerName: "Antigüedad",
      flex: 10,
      renderCell: params => {
        const vencido = horasDesde(params.value) >= OBJETIVO_HORAS;
        return (
          <span title={formatearFechaDDMMYYHHMM(params.value)}>
            {vencido ? (
              <Chip size="small" color="error" label={antiguedad(params.value)} />
            ) : (
              antiguedad(params.value)
            )}
          </span>
        );
      }
    },
    {
      field: "gravedad",
      headerName: "Gravedad",
      flex: 9,
      renderCell: params => (
        <span title={CATEGORIAS[params.row.categoria] ?? "Sin categoría"}>
          {params.value === 1 ? (
            <Chip size="small" color="error" label={etiquetaGravedad(1)} />
          ) : (
            etiquetaGravedad(params.value)
          )}
          <div style={{ opacity: 0.7, fontSize: 12 }}>
            {CATEGORIAS[params.row.categoria] ?? "Sin categoría"}
          </div>
        </span>
      )
    },
    { field: "tipoDescripcion", headerName: "Tipo", flex: 12 },
    { field: "contexto", headerName: "Dónde", flex: 14 },
    {
      field: "autorUsuarioNombre",
      headerName: "Autor",
      flex: 14,
      renderCell: params => (
        <span>
          {params.value || "—"}
          {params.row.sancionesPreviasAutor > 0 &&
            ` (${params.row.sancionesPreviasAutor} sanc.)`}
          {params.row.autorSuspendido && (
            <Chip size="small" label="Suspendido" sx={{ ml: 1 }} />
          )}
        </span>
      )
    },
    {
      field: "contenidoTexto",
      headerName: "Contenido reportado",
      flex: 30,
      renderCell: params => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.3, py: 1 }}>
          {params.row.contenidoImagenUrl && (
            <a
              href={params.row.contenidoImagenUrl}
              target="_blank"
              rel="noreferrer"
            >
              Ver imagen
            </a>
          )}
          {params.row.evidenciaDisponible && (
            <Button size="small" onClick={() => verEvidencia(params.row)}>
              Ver evidencia
            </Button>
          )}{" "}
          {params.value}
          {params.row.contenidoActual && (
            <div style={{ opacity: 0.7 }}>Hoy: {params.row.contenidoActual}</div>
          )}
          {!params.row.contenidoVisible && (
            <div style={{ opacity: 0.7 }}>(ya no se ve en la app)</div>
          )}
          <HiloAnterior mensajes={params.row.hiloAnterior} />
        </Box>
      )
    },
    {
      field: "reportanteUsuarioNombre",
      headerName: "Reportado por",
      flex: 12,
      renderCell: params => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.3, py: 1 }}>
          {params.value}
          {params.row.expulsionReportante && (
            <div style={{ opacity: 0.7 }}>{params.row.expulsionReportante}</div>
          )}
        </Box>
      )
    },
    { field: "motivo", headerName: "Motivo", flex: 14 },
    {
      field: "cantidadReportes",
      headerName: "Reportes",
      flex: 6
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      renderCell: params => (
        <IconButton
          aria-label="Acciones"
          onClick={e => abrirMenu(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ];

  const accionContenidoConfirmable =
    dialogoAccion && ACCION_CONTENIDO[dialogoAccion] && dialogoAccion !== "ignorar";

  return (
    <>
      <div className="flex items-center justify-between gap-4 p-2">
        <div className="page-header">
          <h4 className="page-title">Moderación</h4>
        </div>
      </div>
      <Tabs value={pestania} onChange={(_, v) => setPestania(v)}>
        <Tab label={`Reportes pendientes (${reportes.length})`} />
        <Tab label="Términos filtrados" />
        <Tab
          label={`Sanciones activas (${
            sanciones.suspendidos.length + sanciones.silenciados.length
          })`}
        />
      </Tabs>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />

      {pestania === 0 && (
        <div className="grid-wrapper w-full p-2">
          <p>
            Objetivo en temporada: resolver cada reporte en menos de{" "}
            {OBJETIVO_HORAS} h. Los vencidos se marcan en rojo.
          </p>
          {!cargando && reportes.length === 0 ? (
            <h5>No hay reportes pendientes</h5>
          ) : (
            <DataGrid
              loading={cargando}
              rows={reportes}
              columns={columns}
              getRowHeight={() => "auto"}
              checkboxSelection={false}
            />
          )}
        </div>
      )}

      {pestania === 1 && (
        <div className="w-full p-2">
          <p>
            Todo texto de usuarios (chat, reseñas, respuestas, nombres de usuario
            y de penca) que tenga uno de estos términos se rechaza. Se compara
            sin mayúsculas ni tildes, con "0" como "o", etc., y por palabra
            completa.
          </p>
          <Box sx={{ display: "flex", gap: 1, my: 2 }}>
            <TextField
              size="small"
              label="Nuevo término"
              value={nuevoTermino}
              onChange={e => setNuevoTermino(e.target.value)}
              onKeyDown={e => e.key === "Enter" && agregarTermino()}
            />
            <Button
              variant="contained"
              disabled={!nuevoTermino.trim()}
              onClick={agregarTermino}
            >
              Agregar
            </Button>
          </Box>
          {terminos.length === 0 ? (
            <h5>La lista está vacía</h5>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {terminos.map(t => (
                <Chip
                  key={t.id}
                  label={t.termino}
                  onDelete={() => eliminarTermino(t)}
                  deleteIcon={<DeleteIcon aria-label={`Eliminar ${t.termino}`} />}
                />
              ))}
            </Box>
          )}
        </div>
      )}

      {pestania === 2 && (
        <div className="w-full p-2">
          <h5>Cuentas suspendidas</h5>
          {sanciones.suspendidos.length === 0 ? (
            <p>Ninguna.</p>
          ) : (
            sanciones.suspendidos.map(u => (
              <Box
                key={`s-${u.usuarioId}`}
                sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}
              >
                <span>
                  {u.nombre} — hasta{" "}
                  {new Date(u.suspendidoHasta).getFullYear() >= 9999
                    ? "tiempo indefinido"
                    : formatearFechaDDMMYYHHMM(u.suspendidoHasta)}
                  {u.motivo ? ` (${u.motivo})` : ""}
                </span>
                <Button size="small" onClick={() => levantarSuspension(u)}>
                  Levantar suspensión
                </Button>
              </Box>
            ))
          )}
          <h5 className="mt-4">Silenciados en un chat</h5>
          {sanciones.silenciados.length === 0 ? (
            <p>Ninguno.</p>
          ) : (
            sanciones.silenciados.map(s => (
              <Box
                key={`c-${s.pencaId}-${s.usuarioId}`}
                sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}
              >
                <span>
                  {s.nombre} en {s.pencaNombre}
                </span>
                <Button size="small" onClick={() => quitarSilencio(s)}>
                  Quitar silencio
                </Button>
              </Box>
            ))
          )}
        </div>
      )}

      <Dialog open={!!evidenciaUrl} onClose={cerrarEvidencia} maxWidth="md">
        <DialogTitle>Evidencia</DialogTitle>
        <DialogContent>
          {evidenciaUrl && (
            <img
              src={evidenciaUrl}
              alt="Imagen quitada guardada como evidencia"
              style={{ maxWidth: "100%" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarEvidencia}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={cerrarMenu}>
        {seleccionado &&
          accionesPara(seleccionado).map(accion => (
            <MenuItem key={accion} onClick={() => elegirAccion(accion)}>
              {ETIQUETA_ACCION[accion]}
            </MenuItem>
          ))}
      </Menu>

      <Dialog
        open={dialogoAccion === "advertir"}
        onClose={cerrarDialogoAccion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Advertir a {seleccionado?.autorUsuarioNombre}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            label="Mensaje de advertencia"
            value={textoAdvertencia}
            onChange={e => setTextoAdvertencia(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoAccion}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!textoAdvertencia.trim()}
            onClick={() =>
              sendRequest(
                Servicios.AdvertirUsuario,
                [seleccionado.reporteId, textoAdvertencia],
                cerrarConRefresh
              )
            }
          >
            Enviar advertencia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoAccion === "suspender"}
        onClose={cerrarDialogoAccion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Suspender a {seleccionado?.autorUsuarioNombre}</DialogTitle>
        <DialogContent>
          <p>
            Mientras dure la suspensión no va a poder usar la app con su cuenta
            (ve un aviso con el contacto de soporte).
          </p>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Duración"
            value={diasSuspension}
            onChange={e => setDiasSuspension(Number(e.target.value))}
          >
            <MenuItem value={1}>1 día</MenuItem>
            <MenuItem value={7}>7 días</MenuItem>
            <MenuItem value={30}>30 días</MenuItem>
            <MenuItem value={0}>Indefinida</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Motivo (se le muestra al usuario)"
            value={motivoSuspension}
            onChange={e => setMotivoSuspension(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoAccion}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() =>
              sendRequest(
                Servicios.SuspenderAutorReporte,
                [
                  seleccionado.origen,
                  seleccionado.reporteId,
                  diasSuspension,
                  motivoSuspension
                ],
                cerrarConRefresh
              )
            }
          >
            Suspender
          </Button>
        </DialogActions>
      </Dialog>

      {accionContenidoConfirmable && (
        <TransicionEstadoDialog
          open
          onClose={cerrarConRefresh}
          titulo={ETIQUETA_ACCION[dialogoAccion]}
          acciones={[DESCRIPCION_CONFIRMACION[dialogoAccion](seleccionado)]}
          hallazgos={[]}
          mensajeVacio=""
          puedeConfirmar
          mensajeExito="Listo."
          onConfirmar={() =>
            Servicios.ResolverReporteContenido(
              seleccionado.reporteId,
              ACCION_CONTENIDO[dialogoAccion]
            )
          }
        />
      )}

      {dialogoAccion === "silenciar" && (
        <TransicionEstadoDialog
          open
          onClose={cerrarConRefresh}
          titulo="Silenciar en el chat"
          acciones={[DESCRIPCION_CONFIRMACION.silenciar(seleccionado)]}
          hallazgos={[]}
          mensajeVacio=""
          puedeConfirmar
          mensajeExito="Usuario silenciado en el chat de esta penca."
          onConfirmar={() => Servicios.SilenciarEnChat(seleccionado.reporteId)}
        />
      )}

      {dialogoAccion === "eliminarMensaje" && (
        <TransicionEstadoDialog
          open
          onClose={cerrarConRefresh}
          titulo="Eliminar mensaje"
          acciones={[
            `Se eliminará el mensaje de ${seleccionado?.autorUsuarioNombre} (${seleccionado?.contexto}).`,
            "Esta acción no se puede deshacer."
          ]}
          hallazgos={[]}
          mensajeVacio=""
          puedeConfirmar
          mensajeExito="Mensaje eliminado correctamente."
          onConfirmar={() =>
            Servicios.EliminarMensajeReportado(seleccionado.reporteId)
          }
        />
      )}

      {dialogoAccion === "expulsar" && (
        <TransicionEstadoDialog
          open
          onClose={cerrarConRefresh}
          titulo="Expulsar de la penca"
          acciones={[
            `Se expulsará a ${seleccionado?.autorUsuarioNombre} (${seleccionado?.contexto}).`,
            "En la penca oficial no se puede expulsar: usá Silenciar en el chat.",
            "Esta acción no se puede deshacer."
          ]}
          hallazgos={[]}
          mensajeVacio=""
          puedeConfirmar
          requiereConfirmacionReforzada
          mensajeExito="Usuario expulsado correctamente."
          onConfirmar={() => Servicios.ExpulsarDePenca(seleccionado.reporteId)}
        />
      )}
    </>
  );
};

export default withAuthenticationRequired(ModeracionChat, {
  onRedirecting: () => <Loading />
});
